import type { AdditionalChargeCategory } from '@/shared/api/modules/facilities';
import { chartColor, chartColorPinned } from '@/shared/lib/chart-theme';
import { formatCurrency, formatCurrencyCompact } from '@/shared/lib/currency';
import type { ChartOptions } from 'chart.js';
import type { FC } from 'react';
import { Loader } from '../loader';
import { TooltipElement } from '../tooltip';
import { Typography } from '../typography';
import {
  basicStackedHorizontalBarBackgroundPlugin,
  HorizontalBarChart,
} from './horizontal-bar-chart';

/**
 * Charge-category palette, in paint order: the top three bars take the first
 * three, then each segment of the stacked "Other" bar continues down the list.
 * Sampled from the Figma frame — see `--color-charge-*` in `theme.css`.
 *
 * The tail past index 7 exists only so an unusually long "Other" list keeps
 * getting distinct colors instead of wrapping straight back to charge-1.
 */
const BAR_COLORS = [
  chartColorPinned('--color-charge-1', '#6D92CC'),
  chartColorPinned('--color-charge-2', '#B37FEB'),
  chartColorPinned('--color-charge-3', '#FF85C0'),
  chartColorPinned('--color-charge-4', '#1890FF'),
  chartColorPinned('--color-charge-5', '#FFADD2'),
  chartColorPinned('--color-charge-6', '#D3ADF7'),
  chartColorPinned('--color-charge-7', '#69C0FF'),
  chartColorPinned('--color-charge-8', '#FFD6E7'),
  '#EB2F96',
  '#91D5FF',
  '#12408A',
  '#98B2DD',
  '#C3D3ED',
];

const MAX_LABEL_CHARS = 14;

type AdditionalChargesChartProps = {
  top: AdditionalChargeCategory[];
  other: AdditionalChargeCategory[];
  isLoading?: boolean;
};

/**
 * "Additional charges, $" on the Facilities Financial tab (ST #137).
 *
 * Shares the Top Alerts Categories design — top 3 bars plus a stacked "Other" —
 * but is a single chart over dollars rather than a Market/Facility pair over
 * percentages, so the value axis auto-scales instead of being pinned to 0-100.
 */
export const AdditionalChargesChart: FC<AdditionalChargesChartProps> = ({
  top,
  other,
  isLoading = false,
}) => {
  const hasOther = other.length > 0;
  const labels = hasOther
    ? [...top.map((c) => c.name), 'Other']
    : top.map((c) => c.name);

  // The top bars share one dataset; each "Other" member gets its own so the
  // stack can name and colour its segments individually on hover.
  const datasets = [
    {
      label: 'Additional charges',
      data: [...top.map((c) => c.amountUsd), ...(hasOther ? [null] : [])],
      backgroundColor: top.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]),
      barThickness: 22,
      stack: 'stack',
    },
    // "Other" segments continue the palette where the top bars left off, so no
    // two segments of the stack share a color and the legend/tooltip can tell
    // them apart. (They were previously all one grey.)
    ...other.map((category, i) => ({
      label: category.name,
      data: [...top.map(() => null), category.amountUsd],
      backgroundColor: BAR_COLORS[(top.length + i) % BAR_COLORS.length],
      barThickness: 22,
      stack: 'stack',
    })),
  ];

  // Value labels sit just past the end of each bar, so the widest bar needs the
  // axis to run past it or its label is clipped at the plot edge.
  const otherTotal = other.reduce((sum, c) => sum + c.amountUsd, 0);
  const largestBar = Math.max(...top.map((c) => c.amountUsd), otherTotal, 0);

  const options: ChartOptions<'bar'> = {
    interaction: { mode: 'nearest' as const, intersect: true },
    layout: { padding: { right: 12 } },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        suggestedMax: largestBar * 1.18,
        grid: {
          color: chartColorPinned('--color-neutral-5', '#CCCCCC'),
          drawTicks: false,
        },
        ticks: {
          // Compact ($18K, not $18,000.00): full-precision ticks are long
          // enough that Chart.js rotates them and they collide.
          maxRotation: 0,
          autoSkipPadding: 16,
          callback: (value) => formatCurrencyCompact(Number(value)),
        },
      },
      y: {
        stacked: true,
        grid: {
          color: chartColorPinned('--color-neutral-5', '#CCCCCC'),
          drawTicks: false,
        },
        border: { display: true, color: chartColor('--color-neutral-7') },
        ticks: {
          callback: function (value) {
            const label = this.getLabelForValue(value as number);
            return label.length > MAX_LABEL_CHARS
              ? label.slice(0, MAX_LABEL_CHARS) + '…'
              : label;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        yAlign: 'bottom',
        xAlign: 'center',
        backgroundColor: chartColor('--color-neutral-1'),
        bodyColor: '#000000D8',
        displayColors: false,
        padding: 12,
        borderWidth: 1,
        borderColor: chartColor('--color-neutral-5'),
        bodyFont: { size: 14 },
        filter: (tooltipItem) =>
          tooltipItem.raw !== null && tooltipItem.raw !== undefined,
        callbacks: {
          title: () => '',
          // Inside "Other" the row label is useless, so name the segment itself.
          label: (context) => {
            const isTopBar = context.dataIndex < top.length;
            const name = isTopBar ? context.label : context.dataset.label;
            return `${name}: ${formatCurrency(Number(context.raw), true)}`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: chartColorPinned('--color-neutral-8', '#666666'),
        display: (ctx) => {
          const value = ctx.dataset.data[ctx.dataIndex];
          if (typeof value !== 'number') return false;

          // One total for the whole "Other" stack, drawn by its last segment.
          if (ctx.dataIndex === top.length) {
            const lastIndex = ctx.chart.data.datasets.reduce(
              (last, ds, i) =>
                i > 0 && ds.data[top.length] !== null ? i : last,
              -1
            );
            return ctx.datasetIndex === lastIndex;
          }

          return true;
        },
        formatter: (value, ctx) => {
          if (ctx.dataIndex === top.length) {
            return formatCurrencyCompact(otherTotal);
          }

          return typeof value === 'number' ? formatCurrencyCompact(value) : '';
        },
      },
    },
  };

  return (
    <div className="bg-neutral-1 border-neutral-4 flex h-full w-full flex-col border p-3">
      <div className="flex items-center gap-x-2">
        <Typography as="h5" variant="h5" className="font-medium">
          Additional charges, $
        </Typography>
        <TooltipElement content="Displays total revenue collected from additional services provided during the selected period, grouped by charge type" />
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      ) : top.length > 0 ? (
        <div className="mt-6 h-[180px] w-full">
          <HorizontalBarChart
            options={options}
            data={{ labels, datasets }}
            plugins={[basicStackedHorizontalBarBackgroundPlugin]}
          />
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <Typography className="text-neutral-8">
            No additional charges for this period
          </Typography>
        </div>
      )}
    </div>
  );
};
